import React, { useState } from 'react';
import './PathologyRecord.css';
import './MedicalCertificateRecord.css';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../../../../AppProvider';
import { useAuth } from '../../../../../AuthProvider';
import api from '../../../../../axiosInstance';

const CERT_TYPE_COLORS = {
  'Sick Leave':         { bg: '#fff3cd', color: '#856404', border: '#ffc107' },
  'Fitness':            { bg: '#d1e7dd', color: '#0a3622', border: '#66d348' },
  'Medical Necessity':  { bg: '#cff4fc', color: '#055160', border: '#3CB9D4' },
  'Disability':         { bg: '#f8d7da', color: '#842029', border: '#e63946' },
  'Other':              { bg: '#e2e3e5', color: '#383d41', border: '#adb5bd' },
};

const fmt = (dateStr) =>
  dateStr
    ? new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

const MedicalCertificateRecord = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { patientsMHR, setPatientsMHR, selectedClinic, selectedPatientObj } = useApp();

  let { recordId } = useParams();
  if (recordId === undefined) {
    recordId = patientsMHR[0].recordId;
    navigate(`/DashboardDashboardNavigation/records/${recordId}`);
  }

  const record = patientsMHR.find((r) => r.recordId === recordId);

  const isOwner =
    record.doctorId?.toString() === user?._id?.toString() &&
    record.clinicId?.toString() === selectedClinic?._id?.toString();

  // ---- edit state ----
  const [isEditing, setIsEditing]       = useState(false);
  const [isSaving, setIsSaving]         = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [editDiagnosis, setEditDiagnosis]   = useState([]);
  const [diagInput, setDiagInput]           = useState('');
  const [editFitForDuty, setEditFitForDuty] = useState(false);
  const [editLeaveDays, setEditLeaveDays]   = useState('');
  const [editFromDate, setEditFromDate]     = useState('');
  const [editToDate, setEditToDate]         = useState('');
  const [editPurpose, setEditPurpose]       = useState('');
  const [editRemarks, setEditRemarks]       = useState('');

  // ---- resend state ----
  const [showResend, setShowResend]     = useState(false);
  const [resendStatus, setResendStatus] = useState(null); // null | 'sending' | 'success' | 'error'
  const [resendError, setResendError]   = useState('');

  const startEdit = () => {
    setEditDiagnosis(record.diagnosis ? [...record.diagnosis] : []);
    setEditFitForDuty(record.fitForDuty ?? false);
    setEditLeaveDays(record.leaveDays ?? '');
    setEditFromDate(record.fromDate ? record.fromDate.split('T')[0] : '');
    setEditToDate(record.toDate   ? record.toDate.split('T')[0]   : '');
    setEditPurpose(record.purpose ?? '');
    setEditRemarks(record.remarks ?? '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const body = {
        diagnosis:  editDiagnosis,
        fitForDuty: editFitForDuty,
        leaveDays:  editLeaveDays !== '' ? Number(editLeaveDays) : undefined,
        fromDate:   editFromDate  || undefined,
        toDate:     editToDate    || undefined,
        purpose:    editPurpose   || undefined,
        remarks:    editRemarks   || undefined,
      };
      Object.keys(body).forEach(k => body[k] === undefined && delete body[k]);
      const { data } = await api.put(`/mhr/medicalCertificateRecords/${record._id}`, body);
      setPatientsMHR(prev => prev.map(r => r.recordId === recordId ? { ...r, ...data } : r));
      setIsEditing(false);
    } catch (err) {
      alert(`Error saving: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/mhr/medicalCertificateRecords/${record._id}`);
      const remaining = patientsMHR.filter(r => r.recordId !== recordId);
      setPatientsMHR(remaining);
      navigate(
        remaining.length > 0
          ? `/DashboardDashboardNavigation/records/${remaining[0].recordId}`
          : '/DashboardDashboardNavigation/records'
      );
    } catch (err) {
      alert(`Error deleting: ${err.message}`);
    } finally {
      setConfirmDelete(false);
    }
  };

  const handleResend = async () => {
    setResendStatus('sending');
    setResendError('');
    try {
      await api.post(`/mhr/medicalCertificateRecords/${record._id}/send-pdf`);
      setResendStatus('success');
    } catch (err) {
      setResendError(err.response?.data?.error || err.message || 'Failed to send PDF.');
      setResendStatus('error');
    }
  };

  const closeResend = () => { setShowResend(false); setResendStatus(null); setResendError(''); };

  const showLeaveFields =
    record.certificateType === 'Sick Leave' || record.certificateType === 'Disability';

  const certStyle = CERT_TYPE_COLORS[record.certificateType] || CERT_TYPE_COLORS['Other'];
  const patientName = [selectedPatientObj?.name?.firstName, selectedPatientObj?.name?.lastName].filter(Boolean).join(' ');
  const phone = selectedPatientObj?.contact?.phoneNumber?.phoneNumber || '—';

  // ---- tag input helpers ----
  const addDiagTag = (e) => {
    if (e.key === 'Enter' && diagInput.trim()) {
      e.preventDefault();
      setEditDiagnosis(prev => [...prev, diagInput.trim()]);
      setDiagInput('');
    }
  };
  const removeDiagTag = (i) => setEditDiagnosis(prev => prev.filter((_, idx) => idx !== i));

  return (
    <div className="card-container-main mc-record-page">

      {/* ---- Header ---- */}
      <div className="prescription-record-header">
        <div className="mc-record-title-row">
          <h2 className="card-heading-1">Medical Certificate</h2>
          <span
            className="mc-cert-type-badge"
            style={{ background: certStyle.bg, color: certStyle.color, border: `1.5px solid ${certStyle.border}` }}
          >
            {record.certificateType}
          </span>
        </div>
        {isOwner && !isEditing && (
          <div className="rx-record-actions">
            <button className="rx-record-btn mc-resend-btn" onClick={() => setShowResend(true)}>
              Send WhatsApp
            </button>
            <button className="rx-record-btn rx-record-edit-btn" onClick={startEdit}>Edit</button>
            <button className="rx-record-btn rx-record-delete-btn" onClick={() => setConfirmDelete(true)}>Delete</button>
          </div>
        )}
        {isEditing && (
          <div className="rx-record-actions">
            <button className="rx-record-btn rx-record-cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
            <button className="rx-record-btn rx-record-save-btn" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* ---- Meta card ---- */}
      <div className="rx-meta-card">
        <div className="rx-meta-row">
          {record.doctorName && (
            <div className="rx-meta-item">
              <span className="rx-meta-label">Doctor</span>
              <span className="rx-meta-value">{record.doctorName}</span>
            </div>
          )}
          <div className="rx-meta-item">
            <span className="rx-meta-label">Issue Date</span>
            <span className="rx-meta-value">{fmt(record.dateOfPublish)}</span>
          </div>
          <div className="rx-meta-item">
            <span className="rx-meta-label">Record ID</span>
            <span className="rx-meta-value" style={{ fontSize: 12 }}>{record.recordId}</span>
          </div>
        </div>
        {(record.clinicAdd || record.appointmentId) && (
          <div className="rx-meta-row">
            {record.clinicAdd && (
              <div className="rx-meta-item">
                <span className="rx-meta-label">Clinic</span>
                <span className="rx-meta-value">{record.clinicAdd}</span>
              </div>
            )}
            {record.appointmentId && (
              <div className="rx-meta-item">
                <span className="rx-meta-label">Appointment</span>
                <span className="rx-meta-value rx-meta-appt-id">{record.appointmentId}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ---- Key info grid ---- */}
      <div className="mc-info-grid">
        {/* Fit for Duty */}
        <div className="mc-info-card">
          <span className="mc-info-label">Fit for Duty</span>
          {isEditing ? (
            <div className="mc-edit-toggle-row">
              <button
                className={`mc-edit-toggle ${!editFitForDuty ? 'mc-edit-toggle--active mc-edit-toggle--unfit' : ''}`}
                onClick={() => setEditFitForDuty(false)}
              >No (Unfit)</button>
              <button
                className={`mc-edit-toggle ${editFitForDuty ? 'mc-edit-toggle--active mc-edit-toggle--fit' : ''}`}
                onClick={() => setEditFitForDuty(true)}
              >Yes (Fit)</button>
            </div>
          ) : (
            <span className={`mc-fit-badge ${record.fitForDuty ? 'mc-fit-badge--fit' : 'mc-fit-badge--unfit'}`}>
              {record.fitForDuty ? 'Yes — Fit' : 'No — Unfit'}
            </span>
          )}
        </div>

        {/* Leave Days */}
        {(showLeaveFields || record.leaveDays) && (
          <div className="mc-info-card">
            <span className="mc-info-label">Leave Days</span>
            {isEditing ? (
              <input
                className="mc-record-input"
                type="number"
                min="1"
                value={editLeaveDays}
                onChange={e => {
                  setEditLeaveDays(e.target.value);
                  if (editFromDate && e.target.value) {
                    const end = new Date(editFromDate);
                    end.setDate(end.getDate() + Number(e.target.value) - 1);
                    setEditToDate(end.toISOString().split('T')[0]);
                  }
                }}
              />
            ) : (
              <span className="mc-info-value">{record.leaveDays ?? '—'}</span>
            )}
          </div>
        )}

        {/* From Date */}
        {(showLeaveFields || record.fromDate) && (
          <div className="mc-info-card">
            <span className="mc-info-label">From Date</span>
            {isEditing ? (
              <input
                className="mc-record-input"
                type="date"
                value={editFromDate}
                onChange={e => {
                  setEditFromDate(e.target.value);
                  if (editLeaveDays) {
                    const end = new Date(e.target.value);
                    end.setDate(end.getDate() + Number(editLeaveDays) - 1);
                    setEditToDate(end.toISOString().split('T')[0]);
                  }
                }}
              />
            ) : (
              <span className="mc-info-value">{fmt(record.fromDate)}</span>
            )}
          </div>
        )}

        {/* To Date */}
        {(showLeaveFields || record.toDate) && (
          <div className="mc-info-card">
            <span className="mc-info-label">To Date</span>
            {isEditing ? (
              <input className="mc-record-input" type="date" value={editToDate} onChange={e => setEditToDate(e.target.value)} />
            ) : (
              <span className="mc-info-value">{fmt(record.toDate)}</span>
            )}
          </div>
        )}
      </div>

      {/* ---- Diagnosis ---- */}
      <div className="mc-section">
        <h3 className="mc-section-heading">Diagnosis</h3>
        <hr className="horizontal-line" />
        {isEditing ? (
          <div>
            <input
              className="mc-record-input mc-diag-input"
              placeholder="Type diagnosis and press Enter"
              value={diagInput}
              onChange={e => setDiagInput(e.target.value)}
              onKeyDown={addDiagTag}
            />
            <div className="mc-record-tags">
              {editDiagnosis.map((d, i) => (
                <span key={i} className="mc-record-tag">
                  {d}
                  <span className="mc-record-tag-remove" onClick={() => removeDiagTag(i)}>×</span>
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="mc-record-tags mc-record-tags--view">
            {record.diagnosis?.length
              ? record.diagnosis.map((d, i) => <span key={i} className="mc-record-tag mc-record-tag--view">{d}</span>)
              : <span className="mc-empty-text">—</span>}
          </div>
        )}
      </div>

      {/* ---- Purpose ---- */}
      <div className="mc-section">
        <h3 className="mc-section-heading">Purpose</h3>
        <hr className="horizontal-line" />
        {isEditing ? (
          <textarea
            className="mc-record-textarea"
            rows={2}
            value={editPurpose}
            onChange={e => setEditPurpose(e.target.value)}
          />
        ) : (
          <p className="prescription-para">{record.purpose || '—'}</p>
        )}
      </div>

      {/* ---- Remarks ---- */}
      <div className="mc-section">
        <h3 className="mc-section-heading">Remarks / Clinical Notes</h3>
        <hr className="horizontal-line" />
        {isEditing ? (
          <textarea
            className="mc-record-textarea"
            rows={4}
            value={editRemarks}
            onChange={e => setEditRemarks(e.target.value)}
          />
        ) : (
          <p className="prescription-para">{record.remarks || '—'}</p>
        )}
      </div>

      {/* ---- Resend popup ---- */}
      {showResend && (
        <div className="rx-popup-overlay">
          <div className="rx-resend-popup">
            <h3 className="rx-resend-title">Send Medical Certificate</h3>

            {resendStatus !== 'success' && (
              <p className="rx-resend-msg">
                The certificate PDF will be sent to{' '}
                <strong>{patientName}</strong>'s WhatsApp (<strong>{phone}</strong>).
              </p>
            )}

            {resendStatus === 'sending' && (
              <div className="rx-resend-status rx-resend-status--sending">Sending PDF, please wait…</div>
            )}
            {resendStatus === 'success' && (
              <div className="rx-resend-status rx-resend-status--success">Certificate sent successfully!</div>
            )}
            {resendStatus === 'error' && (
              <div className="rx-resend-status rx-resend-status--error">{resendError}</div>
            )}

            <div className="rx-resend-actions">
              {resendStatus === 'success' ? (
                <button className="rx-record-btn rx-record-cancel-btn" onClick={closeResend}>Close</button>
              ) : (
                <>
                  <button className="rx-record-btn rx-record-cancel-btn" onClick={closeResend} disabled={resendStatus === 'sending'}>Cancel</button>
                  <button className="rx-record-btn rx-resend-send-btn" onClick={handleResend} disabled={resendStatus === 'sending'}>
                    {resendStatus === 'sending' ? 'Sending…' : resendStatus === 'error' ? 'Retry' : 'Send'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---- Delete confirmation ---- */}
      {confirmDelete && (
        <div className="rx-popup-overlay">
          <div className="rx-delete-confirm-popup">
            <h3 className="rx-delete-confirm-title">Delete Certificate?</h3>
            <p className="rx-delete-confirm-msg">This medical certificate will be permanently deleted and cannot be undone.</p>
            <div className="rx-delete-confirm-actions">
              <button className="rx-record-btn rx-record-cancel-btn" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="rx-record-btn rx-record-delete-btn" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicalCertificateRecord;
